import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CliError from '../utilities/CliError';
import useExit from '../utilities/useExit';
import useInput from '../utilities/useInput';

type BuildTableStatus =
  | 'loading'
  | 'show-builds'
  | 'no-builds'
  | 'error'
  | 'selected';

type ExitState = Extract<BuildTableStatus, 'loading' | 'show-builds'>;

const DEFAULT_EXIT_STATES: ExitState[] = ['loading', 'show-builds'];

type LoadResult<T> =
  | T[]
  | {
      status: 'builds';
      builds: T[];
    }
  | {
      status: 'no-builds';
    }
  | {
      status: 'selected';
      selectedId: string;
    };

type HookState<T> = {
  builds: T[];
  error: Error | null;
  status: BuildTableStatus;
  selectedId?: string;
};

export type UseBuildTableStateOptions<T> = {
  exitOnError?: boolean;
  exitOnNoData?: boolean;
  exitStates?: ExitState[];
  load: () => Promise<LoadResult<T>>;
};

export type UseBuildTableStateResult<T> = {
  builds: T[];
  error: Error | null;
  selectItem: (id: string) => void;
  selectedId?: string;
  status: BuildTableStatus;
};

export default function useBuildTableState<T extends Record<string, string>>({
  exitOnError = true,
  exitOnNoData = true,
  exitStates = DEFAULT_EXIT_STATES,
  load,
}: UseBuildTableStateOptions<T>): UseBuildTableStateResult<T> {
  const exit = useExit();
  const [state, setState] = useState<HookState<T>>({
    builds: [],
    error: null,
    status: 'loading',
  });
  const exitRef = useRef(exit);
  useEffect(() => {
    exitRef.current = exit;
  }, [exit]);
  const handleInput = useCallback(
    (_: unknown, key: { escape?: boolean }) => {
      if (key.escape && exitStates.includes(state.status as ExitState)) {
        exitRef.current();
      }
    },
    [exitStates, state.status],
  );

  useInput(handleInput);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      setState((previous) => ({
        ...previous,
        error: null,
        status: 'loading',
      }));

      try {
        const result = await load();
        if (!isMounted) {
          return;
        }

        if (Array.isArray(result)) {
          const builds = result;
          if (builds.length === 0) {
            setState({
              builds: [],
              error: null,
              status: 'no-builds',
            });
            if (exitOnNoData) {
              setTimeout(() => exitRef.current(), 10);
            }
            return;
          }

          setState({
            builds,
            error: null,
            status: 'show-builds',
          });
          return;
        }

        switch (result.status) {
          case 'builds': {
            const { builds } = result;
            if (builds.length === 0) {
              setState({
                builds: [],
                error: null,
                status: 'no-builds',
              });
              if (exitOnNoData) {
                setTimeout(() => exitRef.current(), 10);
              }
            } else {
              setState({
                builds,
                error: null,
                status: 'show-builds',
              });
            }
            break;
          }
          case 'no-builds':
            setState({
              builds: [],
              error: null,
              status: 'no-builds',
            });
            if (exitOnNoData) {
              setTimeout(() => exitRef.current(), 10);
            }
            break;
          case 'selected':
            setState({
              builds: [],
              error: null,
              selectedId: result.selectedId,
              status: 'selected',
            });
            break;
          default:
            setState({
              builds: [],
              error: CliError.from(
                new Error('Unexpected load result from useBuildTableState'),
              ),
              status: 'error',
            });
            if (exitOnError) {
              setTimeout(() => exitRef.current(), 10);
            }
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }
        const cliError = CliError.from(error);
        setState({
          builds: [],
          error: cliError,
          status: 'error',
        });
        if (exitOnError) {
          setTimeout(() => exitRef.current(), 10);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [exitOnError, exitOnNoData, load]);

  const selectItem = useCallback((id: string) => {
    setState((previous) => ({
      ...previous,
      selectedId: id,
      status: 'selected',
    }));
  }, []);

  return useMemo(
    () => ({
      builds: state.builds,
      error: state.error,
      selectItem,
      selectedId: state.selectedId,
      status: state.status,
    }),
    [selectItem, state],
  );
}
